import type { CharacterVariants } from './keyboard.types';

/**
 * Character variants for long-press popup
 * Organized by base character → all possible variants with diacritics
 */

// Lowercase vowels with all diacritics
const A_VARIANTS = ['á', 'à', 'ǎ', 'â', 'ã', 'ā', 'a̧'];
const E_VARIANTS = ['é', 'è', 'ě', 'ê', 'ẽ', 'ē', 'ë'];
const I_VARIANTS = ['í', 'ì', 'ǐ', 'î', 'ĩ', 'ī', 'ï'];
const O_VARIANTS = ['ó', 'ò', 'ǒ', 'ô', 'õ', 'ō', 'ö'];
const U_VARIANTS = ['ú', 'ù', 'ǔ', 'û', 'ũ', 'ū', 'ü', 'ù'];

// Uppercase vowels with all diacritics
const A_UPPER_VARIANTS = ['Á', 'À', 'Ǎ', 'Â', 'Ã', 'Ā', 'Ä'];
const E_UPPER_VARIANTS = ['É', 'È', 'Ě', 'Ê', 'Ẽ', 'Ē', 'Ë'];
const I_UPPER_VARIANTS = ['Í', 'Ì', 'Ǐ', 'Î', 'Ĩ', 'Ī', 'Ï'];
const O_UPPER_VARIANTS = ['Ó', 'Ò', 'Ǒ', 'Ô', 'Õ', 'Ō', 'Ö'];
const U_UPPER_VARIANTS = ['Ú', 'Ù', 'Ǔ', 'Û', 'Ũ', 'Ū', 'Ü'];

// African epsilon (ɛ) variants
const EPSILON_VARIANTS = ['ɛ́', 'ɛ̀', 'ɛ̌', 'ɛ̂', 'ɛ̃', 'ɛ̄'];
const EPSILON_UPPER_VARIANTS = ['Ɛ́', 'Ɛ̀', 'Ɛ̌', 'Ɛ̂', 'Ɛ̃', 'Ɛ̄'];

// African open o (ɔ) variants
const OPEN_O_VARIANTS = ['ɔ́', 'ɔ̀', 'ɔ̌', 'ɔ̂', 'ɔ̃', 'ɔ̄'];
const OPEN_O_UPPER_VARIANTS = ['Ɔ́', 'Ɔ̀', 'Ɔ̌', 'Ɔ̂', 'Ɔ̃', 'Ɔ̄'];

// African upsilon (ʋ) variants
const UPSILON_VARIANTS = ['ʋ́', 'ʋ̀', 'ʋ̌', 'ʋ̂', 'ʋ̃', 'ʋ̄'];
const UPSILON_UPPER_VARIANTS = ['Ʋ́', 'Ʋ̀', 'Ʋ̌', 'Ʋ̂', 'Ʋ̃', 'Ʋ̄'];

// Yoruba underdotted e variants
const E_UNDERDOT_VARIANTS = ['ẹ́', 'ẹ̀', 'ẹ̌', 'ệ', 'ẹ̃', 'ẹ̄'];
const E_UNDERDOT_UPPER_VARIANTS = ['Ẹ́', 'Ẹ̀', 'Ẹ̌', 'Ệ', 'Ẹ̃', 'Ẹ̄'];

// Yoruba underdotted o variants
const O_UNDERDOT_VARIANTS = ['ọ́', 'ọ̀', 'ọ̌', 'ộ', 'ọ̃', 'ọ̄'];
const O_UNDERDOT_UPPER_VARIANTS = ['Ọ́', 'Ọ̀', 'Ọ̌', 'Ộ', 'Ọ̃', 'Ọ̄'];

// Yoruba underdotted s variants
const S_UNDERDOT_VARIANTS = ['ṣ́', 'ṣ̀', 'ṣ̌', 'ṣ̂', 'ṣ̃', 'ṣ̄'];
const S_UNDERDOT_UPPER_VARIANTS = ['Ṣ́', 'Ṣ̀', 'Ṣ̌', 'Ṣ̂', 'Ṣ̃', 'Ṣ̄'];

// Other African characters
const IOTA_VARIANTS = ['ɩ́', 'ɩ̀', 'ɩ̌', 'ɩ̂', 'ɩ̃', 'ɩ̄'];
const IOTA_UPPER_VARIANTS = ['Ɩ́', 'Ɩ̀', 'Ɩ̌', 'Ɩ̂', 'Ɩ̃', 'Ɩ̄'];

const UPSILON_NEAR_CLOSE_VARIANTS = ['ʊ́', 'ʊ̀', 'ʊ̌', 'ʊ̂', 'ʊ̃', 'ʊ̄'];
const UPSILON_NEAR_CLOSE_UPPER_VARIANTS = ['Ʊ́', 'Ʊ̀', 'Ʊ̌', 'Ʊ̂', 'Ʊ̃', 'Ʊ̄'];

// Schwa variants
const SCHWA_VARIANTS = ['ə́', 'ə̀', 'ə̌', 'ə̂', 'ə̃', 'ə̄'];
const SCHWA_UPPER_VARIANTS = ['Ə́', 'Ə̀', 'Ə̌', 'Ə̂', 'Ə̃', 'Ə̄'];

// French specific
const C_VARIANTS = ['ç'];
const C_UPPER_VARIANTS = ['Ç'];

// N variants (for Spanish ñ and African ɲ)
const N_VARIANTS = ['ñ', 'ń', 'ǹ', 'ň', 'ñ̂', 'ɲ'];
const N_UPPER_VARIANTS = ['Ñ', 'Ń', 'Ǹ', 'Ň', 'Ɲ'];

// Digraphs and special sequences
const G_VARIANTS = ['gb', 'ɣ'];
const G_UPPER_VARIANTS = ['Gb', 'GB', 'Ɣ'];

const K_VARIANTS = ['kp'];
const K_UPPER_VARIANTS = ['Kp', 'KP'];

const Y_VARIANTS = ['ny', 'ƴ'];
const Y_UPPER_VARIANTS = ['Ny', 'NY', 'Ƴ'];

// Common punctuation variants
const PERIOD_VARIANTS = ['…', '·', '•'];
const QUOTE_VARIANTS = ['“', '”', '«', '»', '‹', '›'];
const APOSTROPHE_VARIANTS = ['‘', '’', '`'];
const DASH_VARIANTS = ['–', '—', '_'];
const SLASH_VARIANTS = ['\\', '|'];

/**
 * Complete mapping of base characters to variants
 * Used by long-press functionality
 */
export const CHARACTER_VARIANTS: CharacterVariants = {
  // Lowercase vowels
  'a': A_VARIANTS,
  'e': E_VARIANTS,
  'i': I_VARIANTS,
  'o': O_VARIANTS,
  'u': U_VARIANTS,

  // Uppercase vowels
  'A': A_UPPER_VARIANTS,
  'E': E_UPPER_VARIANTS,
  'I': I_UPPER_VARIANTS,
  'O': O_UPPER_VARIANTS,
  'U': U_UPPER_VARIANTS,

  // African vowels - epsilon
  'ɛ': EPSILON_VARIANTS,
  'Ɛ': EPSILON_UPPER_VARIANTS,

  // African vowels - open o
  'ɔ': OPEN_O_VARIANTS,
  'Ɔ': OPEN_O_UPPER_VARIANTS,

  // African vowels - upsilon
  'ʋ': UPSILON_VARIANTS,
  'Ʋ': UPSILON_UPPER_VARIANTS,

  // Yoruba underdotted vowels
  'ẹ': E_UNDERDOT_VARIANTS,
  'Ẹ': E_UNDERDOT_UPPER_VARIANTS,
  'ọ': O_UNDERDOT_VARIANTS,
  'Ọ': O_UNDERDOT_UPPER_VARIANTS,

  // Yoruba underdotted s
  'ṣ': S_UNDERDOT_VARIANTS,
  'Ṣ': S_UNDERDOT_UPPER_VARIANTS,

  // African iota
  'ɩ': IOTA_VARIANTS,
  'Ɩ': IOTA_UPPER_VARIANTS,

  // Near-close vowels
  'ʊ': UPSILON_NEAR_CLOSE_VARIANTS,
  'Ʊ': UPSILON_NEAR_CLOSE_UPPER_VARIANTS,

  // Schwa
  'ə': SCHWA_VARIANTS,
  'Ə': SCHWA_UPPER_VARIANTS,

  // Consonants with variants
  'c': C_VARIANTS,
  'C': C_UPPER_VARIANTS,
  'n': N_VARIANTS,
  'N': N_UPPER_VARIANTS,
  'g': G_VARIANTS,
  'G': G_UPPER_VARIANTS,
  'k': K_VARIANTS,
  'K': K_UPPER_VARIANTS,
  'y': Y_VARIANTS,
  'Y': Y_UPPER_VARIANTS,

  // Punctuation variants
  '.': PERIOD_VARIANTS,
  '"': QUOTE_VARIANTS,
  "'": APOSTROPHE_VARIANTS,
  '-': DASH_VARIANTS,
  '/': SLASH_VARIANTS,
};

/**
 * Check if a character has variants available
 */
export function hasVariants(char: string): boolean {
  return char in CHARACTER_VARIANTS;
}

/**
 * Get variants for a character
 */
export function getVariants(char: string): string[] {
  return CHARACTER_VARIANTS[char] || [];
}
